import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, MoreVertical, Eye, Edit, Trash2, Package, Rocket, TrendingDown, Share2, Trophy, KeyRound } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import QuickPriceDropModal from '../components/common/QuickPriceDropModal';
import ShareModal from '../components/common/ShareModal';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ShareEarnCard = ({ userId }) => {
  const [credits, setCredits] = useState(null);
  const [leaders, setLeaders] = useState([]);

  useEffect(() => {
    if (!userId) return;
    fetch(`${API_BASE}/api/share/credits/${userId}`)
      .then(r => r.json()).then(d => setCredits(d.share_credits ?? 0)).catch(() => setCredits(0));
    fetch(`${API_BASE}/api/share/leaderboard`)
      .then(r => r.json()).then(d => setLeaders(Array.isArray(d) ? d : [])).catch(() => {});
  }, [userId]);

  const medals = ['🥇', '🥈', '🥉'];

  return (
    <div className="relative bg-[#1a3a2a] rounded-2xl p-4 mb-4 overflow-hidden" data-testid="share-earn-card">
      <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '18px 18px' }} />
      <div className="relative">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[9px] uppercase tracking-[0.25em] text-[#d4af37]">Share &amp; Earn</p>
            <p className="font-display text-2xl text-white mt-0.5" data-testid="share-credits-amount">
              ${(credits ?? 0).toFixed(2)}
            </p>
            <p className="text-[11px] text-emerald-200/80">earned from sharing — 50¢ per click on your links</p>
          </div>
          <div className="w-11 h-11 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/40 flex items-center justify-center">
            <Trophy className="w-5 h-5 text-[#d4af37]" />
          </div>
        </div>
        {leaders.length > 0 && (
          <div className="mt-3 pt-3 border-t border-white/10" data-testid="share-leaderboard">
            <p className="text-[9px] uppercase tracking-[0.2em] text-emerald-200/70 mb-1.5">Top Sharers</p>
            <div className="space-y-1">
              {leaders.slice(0, 3).map((l, i) => (
                <div key={l.id} className={`flex items-center justify-between text-xs ${l.id === userId ? 'text-[#f3e5b0] font-semibold' : 'text-emerald-100/90'}`}>
                  <span className="truncate">{medals[i] || '·'} {l.name}{l.id === userId ? ' (you)' : ''}</span>
                  <span>${l.share_credits.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MyListingsPage = () => {
  const navigate = useNavigate();
  const { products, user, isAuthenticated, deleteProduct, updateProduct } = useApp();
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [priceDropProduct, setPriceDropProduct] = useState(null);
  const [shareProduct, setShareProduct] = useState(null);
  const [sellerProducts, setSellerProducts] = useState(null);

  // Own listings fetched directly (includes Private Collection / flagged 18+ items hidden from the public list)
  useEffect(() => {
    if (!user?.id) return;
    fetch(`${API_BASE}/api/products/seller/${user.id}?include_adult=true`)
      .then(r => r.json())
      .then(d => { if (Array.isArray(d)) setSellerProducts(d); })
      .catch(() => {});
  }, [user?.id]);

  const movePcListing = async (productId) => {
    try {
      const res = await fetch(`${API_BASE}/api/private-collection/listing-fee?user_id=${user.id}&product_id=${productId}`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Something went wrong');
      if (data.live) {
        toast.success('Your piece is live in The Private Collection');
        setSellerProducts(prev => (prev || []).map(p => p.id === productId ? { ...p, adult_status: 'live' } : p));
      } else if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  // Self-healing promotion activation when returning from Stripe (covers missed webhooks)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('promotion') !== 'success') return;
    const sessionId = params.get('session_id');
    window.history.replaceState({}, '', '/my-listings');
    if (!sessionId) return;
    const verify = async (attempt = 0) => {
      try {
        const res = await fetch(`${API_BASE}/api/promotions/verify?session_id=${sessionId}`);
        const data = await res.json();
        if (data.status === 'active') {
          toast.success(`🚀 Your ${data.promotion_type || 'promotion'} is live!`);
        } else if (attempt < 3) {
          setTimeout(() => verify(attempt + 1), 4000);
        } else {
          toast.error("We couldn't confirm your payment yet — it will activate automatically once confirmed");
        }
      } catch {
        if (attempt < 3) setTimeout(() => verify(attempt + 1), 4000);
      }
    };
    verify();
  }, []);

  if (!isAuthenticated) {
    return (
      <Layout title="My Listings">
        <div className="flex flex-col items-center justify-center py-20 px-4">
          <Package className="w-16 h-16 text-gray-300 mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Sign in to view listings</h2>
          <p className="text-gray-500 text-center mb-4">Create an account to start selling your items</p>
          <Button onClick={() => navigate('/login')} className="bg-minted-600 hover:bg-minted-700">
            Sign In
          </Button>
        </div>
      </Layout>
    );
  }

  // Check both seller_id (from API) and sellerId (camelCase) for compatibility
  const myProducts = sellerProducts !== null
    ? sellerProducts
    : products.filter(p => 
        p.seller_id === user?.id || p.sellerId === user?.id
      );
  const activeListings = myProducts.filter(p => !p.sold);
  const soldListings = myProducts.filter(p => p.sold);

  const handleDelete = async (productId) => {
    const ok = await deleteProduct(productId);
    if (!ok) {
      toast.error("Couldn't delete the listing — please check your connection and try again");
      return;
    }
    setSellerProducts(prev => prev ? prev.filter(p => p.id !== productId) : prev);
    toast.success('Listing deleted');
    setDeleteConfirm(null);
  };

  const ListingCard = ({ product }) => (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      {product.is_adult && product.adult_status === 'pending_payment' && (
        <div className="px-3 py-2.5 flex items-center justify-between gap-2" style={{ backgroundColor: '#14100e' }} data-testid={`pc-pending-banner-${product.id}`}>
          <div className="flex items-center gap-2 min-w-0">
            <KeyRound className="w-4 h-4 shrink-0" style={{ color: '#c9a962' }} strokeWidth={1.3} />
            <p className="text-[11px] leading-tight truncate" style={{ color: '#d8ccbd' }}>This piece belongs in <span style={{ color: '#c9a962' }}>The Private Collection</span> (18+). It's hidden until placed.</p>
          </div>
          <button onClick={() => movePcListing(product.id)} data-testid={`pc-move-btn-${product.id}`}
            className="text-[10px] uppercase tracking-wider px-2.5 py-1.5 rounded-sm shrink-0 font-semibold"
            style={{ backgroundColor: '#c9a962', color: '#14100e' }}>
            Move it there
          </button>
        </div>
      )}
      {product.is_adult && product.adult_status === 'live' && (
        <div className="px-3 py-1.5 flex items-center gap-2" style={{ backgroundColor: '#14100e' }}>
          <KeyRound className="w-3.5 h-3.5" style={{ color: '#c9a962' }} strokeWidth={1.3} />
          <p className="text-[10px] uppercase tracking-wider" style={{ color: '#c9a962' }}>Live in The Private Collection</p>
        </div>
      )}
      <div className="flex">
        <div className="w-24 h-24 flex-shrink-0">
          <img
            src={product.images[0]}
            alt={product.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 p-3">
          <div className="flex items-start justify-between">
            <div className="flex-1 pr-2">
              <h3 className="font-medium text-gray-900 text-sm line-clamp-1">{product.title}</h3>
              <p className="text-lg font-bold text-minted-600">{({ GBP: '£', USD: 'US$', EUR: '€', NZD: 'NZ$', CAD: 'CA$' }[product.listing_currency]) || '$'}{product.price}</p>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="secondary" className="text-[10px]">{product.condition}</Badge>
                {product.sold && (
                  <Badge className="bg-gray-600 text-white text-[10px]">Sold</Badge>
                )}
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-1 hover:bg-gray-100 rounded" data-testid={`listing-menu-btn-${product.id}`}>
                  <MoreVertical className="w-5 h-5 text-gray-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate(`/product/${product.id}`)}>
                  <Eye className="w-4 h-4 mr-2" />
                  View
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShareProduct(product)} className="text-emerald-700" data-testid={`share-listing-${product.id}`}>
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setPriceDropProduct(product)} className="text-orange-600">
                  <TrendingDown className="w-4 h-4 mr-2" />
                  Drop Price
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate(`/promote/${product.id}`)}>
                  <Rocket className="w-4 h-4 mr-2" />
                  Promote
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate(`/edit-listing/${product.id}`)} data-testid={`edit-listing-${product.id}`}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setDeleteConfirm(product)}
                  className="text-red-600"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Eye className="w-3 h-3" />
              {product.views}
            </span>
            <span>{product.createdAt}</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Layout title="My Listings">
      {/* Header with Add Button */}
      <div className="bg-white px-4 py-4 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-bold text-lg text-gray-900">My Listings</h2>
            <p className="text-sm text-gray-500">{activeListings.length} active, {soldListings.length} sold</p>
          </div>
          <Button
            onClick={() => navigate('/sell')}
            className="bg-minted-600 hover:bg-minted-700 gap-1"
            size="sm"
          >
            <Plus className="w-4 h-4" />
            New Listing
          </Button>
        </div>
      </div>

      {myProducts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 px-4">
          <Package className="w-16 h-16 text-gray-300 mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">No listings yet</h2>
          <p className="text-gray-500 text-center mb-4">Start selling by creating your first listing</p>
          <Button onClick={() => navigate('/sell')} className="bg-minted-600 hover:bg-minted-700">
            <Plus className="w-4 h-4 mr-2" />
            Create Listing
          </Button>
        </div>
      ) : (
        <div className="p-4 space-y-3">
          <ShareEarnCard userId={user?.id} />
          {/* Active Listings */}
          {activeListings.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Active ({activeListings.length})</h3>
              <div className="space-y-3">
                {activeListings.map(product => (
                  <ListingCard key={product.id} product={product} />
                ))}
              </div>
            </div>
          )}

          {/* Sold Listings */}
          {soldListings.length > 0 && (
            <div className="mt-6">
              <h3 className="font-semibold text-gray-500 mb-3">Sold ({soldListings.length})</h3>
              <div className="space-y-3 opacity-70">
                {soldListings.map(product => (
                  <ListingCard key={product.id} product={product} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Listing</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteConfirm?.title}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleDelete(deleteConfirm?.id)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Price Drop Modal */}
      <QuickPriceDropModal
        isOpen={!!priceDropProduct}
        onClose={() => setPriceDropProduct(null)}
        product={priceDropProduct}
        userId={user?.id}
        onPriceUpdated={(productId, newPrice) => {
          if (updateProduct) {
            updateProduct(productId, { price: newPrice });
          }
        }}
      />

      {/* Share Listing Modal */}
      <ShareModal
        isOpen={!!shareProduct}
        onClose={() => setShareProduct(null)}
        product={shareProduct}
      />
    </Layout>
  );
};

export default MyListingsPage;